import React, { useMemo, useState } from 'react';
import { Alert, Button, Card, Input, InputNumber, Modal, Space, Table, Tag, Typography, message } from 'antd';
import type { SortCountDiffRowDTO, SortCountScanEventDTO, SortCountSnapshotDTO, SortCountSummaryDTO } from '../../types/sortAndCount';

const { Text } = Typography;

type Props = {
  title: string;
  instructionCode?: string;
  canOperate?: boolean;
  rows: SortCountDiffRowDTO[];
  events: SortCountScanEventDTO[];
  summary: SortCountSummaryDTO;
  snapshot: SortCountSnapshotDTO;
  validation: { pass: boolean; errors: string[]; warnings: string[] };
  onScan: (input: { scanValue: string; qty?: number; source?: 'scan' | 'manual' | 'import' }) => { ok: boolean; reason?: string };
  onRemoveEvent: (id: string) => void;
  onClearEvents: () => void;
  onSubmitRequest?: () => Promise<void> | void;
  extraActions?: React.ReactNode;
};

const SortAndCountPanel: React.FC<Props> = ({
  title,
  instructionCode,
  canOperate,
  rows,
  events,
  summary,
  snapshot,
  validation,
  onScan,
  onRemoveEvent,
  onClearEvents,
  onSubmitRequest,
  extraActions,
}) => {
  const [scanValue, setScanValue] = useState('');
  const [qty, setQty] = useState<number>(1);

  const safeCanOperate = canOperate !== false;
  const orderedRows = useMemo(() => {
    const arr = [...(Array.isArray(rows) ? rows : [])];
    arr.sort((a, b) => {
      const aDone = Number(a?.diff_qty || 0) <= 0;
      const bDone = Number(b?.diff_qty || 0) <= 0;
      if (aDone !== bDone) return aDone ? 1 : -1;
      return String(a?.target_label || '').localeCompare(String(b?.target_label || ''));
    });
    return arr;
  }, [rows]);

  const handleScan = () => {
    if (!safeCanOperate) return;
    const result = onScan({
      scanValue: scanValue.trim(),
      qty: Math.max(1, Number(qty || 1)),
      source: 'scan',
    });
    if (!result?.ok) {
      if (result?.reason === 'target_not_found') message.warning('扫描值未匹配到目标物');
      else message.warning('请先输入扫描值');
      return;
    }
    setScanValue('');
  };

  const openSnapshot = () => {
    Modal.info({
      title: `分拣清点快照${instructionCode ? ` - ${instructionCode}` : ''}`,
      width: 980,
      icon: null,
      content: (
        <div style={{ marginTop: 8 }}>
          <div style={{ marginBottom: 8, color: '#595959' }}>
            目标 {snapshot.summary.total_targets} 项，已完成 {snapshot.summary.done_targets} 项，预期总数 {snapshot.summary.expected_total}，已清点 {snapshot.summary.counted_total}。
          </div>
          <Table
            size="small"
            rowKey={(row: any) => String(row?.target_code || '')}
            pagination={{ pageSize: 10, showSizeChanger: true }}
            dataSource={snapshot.rows || []}
            columns={[
              { title: '目标物', dataIndex: 'target_label', key: 'target_label' },
              { title: '分拣标识', dataIndex: 'sorting_mark', key: 'sorting_mark', width: 140, render: (v: any) => v || '-' },
              { title: '目标数量', dataIndex: 'expected_qty', key: 'expected_qty', width: 100, align: 'right' as any },
              { title: '已清点', dataIndex: 'counted_qty', key: 'counted_qty', width: 100, align: 'right' as any },
              {
                title: '差额',
                dataIndex: 'diff_qty',
                key: 'diff_qty',
                width: 90,
                align: 'right' as any,
                render: (value: any) => {
                  const num = Number(value || 0);
                  return <span style={num === 0 ? undefined : { color: num > 0 ? '#cf1322' : '#0958d9', fontWeight: 700 }}>{num}</span>;
                },
              },
            ]}
          />
        </div>
      ),
    });
  };

  return (
    <Card
      title={title}
      extra={(
        <Space>
          <Button onClick={openSnapshot}>预览快照</Button>
          {extraActions}
        </Space>
      )}
    >
      <Space style={{ marginBottom: 12, width: '100%', justifyContent: 'space-between' }} wrap>
        <Space wrap>
          <Text strong>{instructionCode || '未绑定指令上下文'}</Text>
          <Tag color={summary.diff_total === 0 ? 'green' : (summary.diff_total > 0 ? 'red' : 'processing')}>
            差额 {summary.diff_total}
          </Tag>
          <Tag>目标 {summary.total_targets}</Tag>
          <Tag color="green">完成 {summary.done_targets}</Tag>
        </Space>
        <Space>
          <Input
            style={{ width: 220 }}
            allowClear
            disabled={!safeCanOperate}
            value={scanValue}
            onChange={(e) => setScanValue(e.target.value)}
            onPressEnter={handleScan}
            placeholder="扫描目标物编码"
          />
          <InputNumber
            min={1}
            disabled={!safeCanOperate}
            value={qty}
            onChange={(v) => setQty(Number(v || 1))}
            addonBefore="数量"
          />
          <Button type="primary" disabled={!safeCanOperate} onClick={handleScan}>录入清点</Button>
        </Space>
      </Space>

      {validation.errors.length > 0 ? (
        <Alert type="error" style={{ marginBottom: 12 }} showIcon message={validation.errors.join('；')} />
      ) : null}
      {validation.warnings.length > 0 ? (
        <Alert type="warning" style={{ marginBottom: 12 }} showIcon message={validation.warnings.join('；')} />
      ) : null}

      <Table
        size="small"
        rowKey={(row: any) => String(row?.target_code || '')}
        dataSource={orderedRows}
        pagination={{ pageSize: 10, showSizeChanger: true }}
        columns={[
          { title: '目标物', dataIndex: 'target_label', key: 'target_label', width: 220 },
          { title: '分拣标识', dataIndex: 'sorting_mark', key: 'sorting_mark', width: 140, render: (v: any) => v || '-' },
          { title: '目标数量', dataIndex: 'expected_qty', key: 'expected_qty', width: 100, align: 'right' as any },
          { title: '已清点', dataIndex: 'counted_qty', key: 'counted_qty', width: 100, align: 'right' as any },
          {
            title: '差额',
            dataIndex: 'diff_qty',
            key: 'diff_qty',
            width: 90,
            align: 'right' as any,
            render: (value: any) => {
              const num = Number(value || 0);
              return <span style={num === 0 ? undefined : { color: num > 0 ? '#cf1322' : '#0958d9', fontWeight: 700 }}>{num}</span>;
            },
          },
          {
            title: '状态',
            dataIndex: 'done',
            key: 'done',
            width: 100,
            align: 'center' as any,
            render: (_: any, row: any) => (Number(row?.diff_qty || 0) <= 0 ? <Tag color="green">已达成</Tag> : <Tag color="orange">待完成</Tag>),
          },
        ]}
      />

      <div style={{ marginTop: 12, display: 'flex', justifyContent: 'space-between', gap: 12 }}>
        <Space>
          <Button disabled={!safeCanOperate || events.length === 0} onClick={onClearEvents}>清空本次记录</Button>
          <Text type="secondary">操作记录 {events.length} 条</Text>
        </Space>
        <Button type="primary" disabled={!safeCanOperate || !onSubmitRequest} onClick={onSubmitRequest}>提交当前分拣清点</Button>
      </div>

      <Table
        style={{ marginTop: 12 }}
        size="small"
        rowKey={(row: any) => String(row?.id || '')}
        dataSource={events}
        pagination={{ pageSize: 8, showSizeChanger: true }}
        columns={[
          { title: '时间', dataIndex: 'operated_at', key: 'operated_at', width: 180 },
          { title: '扫描值', dataIndex: 'scan_value', key: 'scan_value', width: 220 },
          { title: '目标物', dataIndex: 'target_code', key: 'target_code', width: 220 },
          { title: '数量', dataIndex: 'qty', key: 'qty', width: 80, align: 'right' as any },
          { title: '来源', dataIndex: 'source', key: 'source', width: 90 },
          {
            title: '操作',
            key: 'actions',
            width: 90,
            render: (_: any, row: any) => (
              <Button
                size="small"
                danger
                disabled={!safeCanOperate}
                onClick={() => onRemoveEvent(String(row?.id || ''))}
              >
                删除
              </Button>
            ),
          },
        ]}
      />
    </Card>
  );
};

export default SortAndCountPanel;

